<template>
  <PmRequestOverviewDetailsPure
    :is-loading="isLoading"
    :can-be-approved="canBeApproved"
    :start-date="dates?.start"
    :end-date="dates?.end"
    :mode="mode"
    @approve="onApprove"
    @reject="onReject"
  >
    <PmRequestDetail
      v-if="selectedRequestId && selectedRequestType"
      :id="selectedRequestId"
      :type="selectedRequestType"
    />
  </PmRequestOverviewDetailsPure>

  <PmDialogRequestApprovePure
    v-if="
      xstate.snapshot.matches('approve') && xstate.snapshot.context.requestType
    "
    :approval-type="xstate.snapshot.context.approvalType"
    :request-type="xstate.snapshot.context.requestType"
    :operation="xstate.snapshot.context.operation"
    :initial-form-data="{
      selectedTypeId: currentLeaveRequestQuery.result.value?.leaveRequest?.type,
    }"
    :is-loading="xstate.snapshot.matches({ approve: 'saving' })"
    :error-message="xstate.snapshot.context.error"
    :error-details="xstate.snapshot.context.errorDetails"
    @cancel="xstate.send({ type: 'cancel' })"
    @submit="(variables) => xstate.send({ type: 'confirmApprove', variables })"
  />

  <PmDialogRequestRejectPure
    v-if="
      xstate.snapshot.matches('reject') && xstate.snapshot.context.requestType
    "
    :request-type="xstate.snapshot.context.requestType"
    :operation="xstate.snapshot.context.operation"
    :is-loading="xstate.snapshot.matches({ reject: 'saving' })"
    :error-message="xstate.snapshot.context.error"
    :error-details="xstate.snapshot.context.errorDetails"
    @cancel="xstate.send({ type: 'cancel' })"
    @submit="(variables) => xstate.send({ type: 'confirmReject', variables })"
  />

  <!-- <PmDialogRequestApprovePure
    v-if="
      xstate.snapshot.matches('confirmCancelLeaveRequest') &&
      xstate.snapshot.context.requestType &&
      xstate.snapshot.context.operation === 'confirmCancelLeaveRequest'
    "
    :request-type="xstate.snapshot.context.requestType"
    :operation="xstate.snapshot.context.operation"
    :is-loading="
      xstate.snapshot.matches({ confirmCancelLeaveRequest: 'saving' })
    "
    :error-message="xstate.snapshot.context.error"
    :error-details="xstate.snapshot.context.errorDetails"
    @cancel="xstate.send({ type: 'cancel' })"
    @submit="
      (variables) =>
        xstate.send({ type: 'confirmConfirmCancelLeaveRequest', variables })
    "
  />

  <PmDialogRequestRejectPure
    v-if="
      xstate.snapshot.matches('rejectCancelLeaveRequest') &&
      xstate.snapshot.context.requestType &&
      xstate.snapshot.context.operation === 'rejectCancelLeaveRequest'
    "
    :request-type="xstate.snapshot.context.requestType"
    :operation="xstate.snapshot.context.operation"
    :is-loading="
      xstate.snapshot.matches({ rejectCancelLeaveRequest: 'saving' })
    "
    :error-message="xstate.snapshot.context.error"
    :error-details="xstate.snapshot.context.errorDetails"
    @cancel="xstate.send({ type: 'cancel' })"
    @submit="
      (variables) =>
        xstate.send({ type: 'confirmRejectCancelLeaveRequest', variables })
    "
  /> -->
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { fromPromise } from 'xstate5'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { useStore } from 'vuex'
import type { Get } from 'type-fest'
import { orderBy } from 'lodash-es'
import { isBefore } from 'date-fns'

import PmRequestOverviewDetailsPure from '@/components/PmRequestOverview/PmRequestOverviewDetailsPure.vue'
import PmRequestDetail from '@/components/PmRequestDetail/PmRequestDetail.vue'
import { useXState } from '@/composition/useXState5'
import { PmRequestOverviewDetailsState } from '@/components/PmRequestOverview/PmRequestOverviewDetailsState'
import type { Nilable } from '@/types/misc'
import {
  ApproveLeaveRequestDocument,
  type ApproveLeaveRequestMutationVariables,
  LeaveRequestType,
  LeaveRequestDetailsDocument,
  type LeaveRequestDetailsQuery,
  RejectLeaveRequestDocument,
  type RejectLeaveRequestMutationVariables,
  CurrentUserDocument,
  LeaveRequestAction,
  LeaveRequestState,
  ApproveExternalServiceRequestDocument,
  type ApproveExternalServiceRequestMutationVariables,
  RejectExternalServiceRequestDocument,
  ExternalServiceRequestDetailsDocument,
  type ExternalServiceRequestDetailsQuery,
  ExternalServiceRequestAction,
  RejectCancelLeaveRequestDocument,
  type RejectCancelLeaveRequestMutationVariables,
  ApproveCancelLeaveRequestDocument,
  type ApproveCancelLeaveRequestMutationVariables,
} from '../../../generated/graphql'
import { throwFriendlyError } from '@/functional/friendlyErrors'
import PmDialogRequestApprovePure, {
  type Emits as EmitsDialogRequestApprovePure,
} from '@/components/PmRequestOverview/PmDialogRequestApprove/PmDialogRequestApprovePure.vue'
import PmDialogRequestRejectPure, {
  type Emits as EmitsDialogRequestRejectPure,
} from '@/components/PmRequestOverview/PmDialogRequestReject/PmDialogRequestRejectPure.vue'
import { isValidDate, parseServerDateString } from '@/utilities/date'

export interface Props {
  selectedRequestId?: Nilable<number>
  selectedRequestType?:
    | 'leaveRequest'
    | 'externalServiceRequest'
    | 'expenseReport'
  mode?: 'current' | 'archive'
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'current',
})

const store = useStore()

const xstate = useXState(
  PmRequestOverviewDetailsState.provide({
    actions: {
      showApproveSuccessNotification: () => {
        store.commit('notification/add', {
          variant: 'success',
          title: 'Antrag genehmigt',
        })
      },

      showRejectSuccessNotification: () => {
        store.commit('notification/add', {
          variant: 'success',
          title: 'Antrag abgelehnt',
        })
      },

      showConfirmCancelLeaveRequestSuccessNotification: () => {
        store.commit('notification/add', {
          variant: 'success',
          title: 'Stornierung genehmigt',
        })
      },

      showRejectCancelLeaveRequestSuccessNotification: () => {
        store.commit('notification/add', {
          variant: 'success',
          title: 'Stornierung abgelehnt',
        })
      },
    },

    actors: {
      approveRequest: fromPromise(async ({ input }) =>
        approveRequest(input.variables)
      ),

      rejectRequest: fromPromise(async ({ input }) =>
        rejectRequest(input.variables)
      ),
    },
  }),
  {
    useInspector: false,
  }
)

const currentUserQuery = useQuery(CurrentUserDocument)
const currentUserId = computed(() => currentUserQuery.result.value?.user?.id)

/**
 * This awkwardly loads the data additionally to PmMRequestDetail component
 * but there is not good way to access data needed here otherwise
 * @todo Optimize
 */
const currentLeaveRequestQuery = useQuery(
  LeaveRequestDetailsDocument,
  // @ts-expect-error https://github.com/vuejs/apollo/issues/1243
  () => ({
    id: props.selectedRequestId,
  }),
  () => ({
    enabled:
      props.selectedRequestId !== undefined &&
      props.selectedRequestType === 'leaveRequest',
  })
)

const currentLeaveRequest = computed(
  () => currentLeaveRequestQuery.result.value?.leaveRequest
)

const currentApprovalType = computed(
  () => currentLeaveRequestQuery.result.value?.leaveRequest?.approvalType
)

const isLoading = computed(() => {
  if (props.selectedRequestType === 'leaveRequest')
    return currentLeaveRequestQuery.loading.value

  return false
})

/**
 * This awkwardly loads the data additionally to PmMRequestDetail component
 * but there is not good way to access data needed here otherwise
 * @todo Optimize
 */
const currentExternalServiceRequestQuery = useQuery(
  ExternalServiceRequestDetailsDocument,
  // @ts-expect-error https://github.com/vuejs/apollo/issues/1243
  () => ({
    id: props.selectedRequestId,
  }),
  () => ({
    enabled:
      props.selectedRequestId !== undefined &&
      props.selectedRequestType === 'externalServiceRequest',
  })
)

const currentExternalServiceRequest = computed(
  () => currentExternalServiceRequestQuery.result.value?.externalServiceRequest
)

/**
 * Start-/EndDate of displayed request
 */
const dates = computed(() => {
  if (props.selectedRequestType === 'leaveRequest') {
    if (!currentLeaveRequest.value) return undefined

    return {
      start: parseServerDateString(currentLeaveRequest.value?.startDate),
      end: parseServerDateString(currentLeaveRequest.value?.endDate),
    }
  }

  if (props.selectedRequestType === 'externalServiceRequest') {
    if (!currentExternalServiceRequest.value) return undefined

    return {
      start: parseServerDateString(
        currentExternalServiceRequest.value?.startDate
      ),
      end: parseServerDateString(currentExternalServiceRequest.value?.endDate),
    }
  }

  return undefined
})

/**
 * Approve
 */

/**
 * Check if the leave request can be approved. This is for example the case
 * - when a request has already been approved by the current user, it can't be approved again
 */
const canBeApproved = computed(() => {
  if (
    props.selectedRequestType === 'leaveRequest' &&
    currentLeaveRequest.value
  ) {
    return canLeaveRequestBeApproved(currentLeaveRequest.value)
  }

  if (
    props.selectedRequestType === 'externalServiceRequest' &&
    currentExternalServiceRequest.value
  ) {
    return canExternalServiceRequestBeApproved(
      currentExternalServiceRequest.value
    )
  }

  return true
})

/**
 * If the last action of the current user was `approved` the
 * user should not be able to approve again
 */
function canLeaveRequestBeApproved(
  leaveRequest: Get<LeaveRequestDetailsQuery, 'leaveRequest'>
) {
  if (!currentUserId.value) return false
  if (!leaveRequest?.communications) return true

  const actionsOfUser = leaveRequest.communications?.filter((communication) => {
    return communication.user.id === currentUserId.value
  })

  // User has not taken any actions yet
  if (actionsOfUser.length === 0) return true

  const lastActionOfUser = orderBy(
    actionsOfUser,
    (communication) => parseServerDateString(communication.timestamp),
    ['desc']
  )[0]

  const actionsOfRequester = leaveRequest.communications?.filter(
    (communication) => {
      return communication.user.id === leaveRequest.user.id
    },
    ['desc']
  )

  const lastActionOfRequester = orderBy(
    actionsOfRequester,
    (communication) => parseServerDateString(communication.timestamp),
    ['desc']
  )[0]

  /**
   * If the last action of the user was not an `accept` he should be able to
   * accept in any case. This usually only can happen when there is no `requestCancel`
   * by the requester
   */
  if (lastActionOfUser.action !== LeaveRequestAction.accept) return true

  /**
   * If there is no `requestCancel` from the requester, the user
   * has nothing to accept anymore
   */
  if (lastActionOfRequester.action !== LeaveRequestAction.requestCancel) {
    return false
  }

  /**
   * Here we are in the state where there is an `requestCancel` from the requester, we need
   * to figure out if the `accepted` of the user happened after this
   */
  const lastActionOfUserTimestamp = parseServerDateString(
    lastActionOfUser.timestamp
  )
  if (!isValidDate(lastActionOfUserTimestamp)) {
    throw new Error('lastActionOfUserTimestamp is not a valid date')
  }

  const lastActionOfRequesterTimestamp = parseServerDateString(
    lastActionOfRequester.timestamp
  )
  if (!isValidDate(lastActionOfRequesterTimestamp)) {
    throw new Error('lastActionOfRequesterTimestamp is not a valid date')
  }

  const userAlreadyProcessedRequestCancel =
    isBefore(lastActionOfRequesterTimestamp, lastActionOfUserTimestamp) &&
    (lastActionOfUser.action === LeaveRequestAction.accept ||
      lastActionOfUser.action === LeaveRequestAction.decline)

  if (userAlreadyProcessedRequestCancel) return false

  return true
}

/**
 * If the last action of the current user was `approved` the
 * user should not be able to approve again
 */
function canExternalServiceRequestBeApproved(
  externalServiceRequest: Get<
    ExternalServiceRequestDetailsQuery,
    'externalServiceRequest'
  >
) {
  if (!currentUserId.value) return false
  if (!externalServiceRequest?.communications) return true

  const actionsOfUser = externalServiceRequest.communications?.filter(
    (communication) => {
      return communication.user.id === currentUserId.value
    }
  )
  if (actionsOfUser.length === 0) return true

  const lastActionOfUser = orderBy(actionsOfUser, (communication) =>
    parseServerDateString(communication.timestamp)
  )[0]

  return lastActionOfUser.action !== ExternalServiceRequestAction.accept
}

const approveLeaveRequestMutation = useMutation(ApproveLeaveRequestDocument)

async function approveLeaveRequest(
  variables: ApproveLeaveRequestMutationVariables
) {
  try {
    await approveLeaveRequestMutation.mutate(variables)
  } catch (error) {
    throwFriendlyError(error)
  }
}

const approveCancelLeaveRequestMutation = useMutation(
  ApproveCancelLeaveRequestDocument
)

async function approveCancelLeaveRequest(
  variables: ApproveCancelLeaveRequestMutationVariables
) {
  try {
    await approveCancelLeaveRequestMutation.mutate(variables)
  } catch (error) {
    throwFriendlyError(error)
  }
}

const approveExternalServiceRequestMutation = useMutation(
  ApproveExternalServiceRequestDocument
)

async function approveExternalServiceRequest(
  variables: ApproveExternalServiceRequestMutationVariables
) {
  try {
    await approveExternalServiceRequestMutation.mutate(variables)
  } catch (error) {
    throwFriendlyError(error)
  }
}

const operation = computed(() => {
  if (props.selectedRequestType === 'leaveRequest') {
    if (
      currentLeaveRequest.value?.state &&
      currentLeaveRequest.value.state === LeaveRequestState.cancelRequested
    ) {
      return 'processCancelLeaveRequest'
    }
  }

  return undefined
})

function onApprove() {
  if (!props.selectedRequestId)
    throw new Error('selectedRequestId is undefined')
  if (!props.selectedRequestType)
    throw new Error('selectedRequestType is undefined')
  if (props.selectedRequestType === 'expenseReport') return

  xstate.value.send({
    type: 'approve',
    variables: {
      id: props.selectedRequestId,
      type: props.selectedRequestType,
      operation: operation.value,
      approvalType: currentApprovalType.value,
    },
  })
}

export type ApproveRequestVariables = {
  id: number
  type: 'leaveRequest' | 'externalServiceRequest'
  operation: typeof operation.value
  formData: EmitsDialogRequestApprovePure['submit']
}

async function approveRequest(options: ApproveRequestVariables) {
  const type = options.formData.type
    ? LeaveRequestType[options.formData.type]
    : undefined

  if (options.type === 'leaveRequest') {
    if (options.operation === 'processCancelLeaveRequest') {
      return approveCancelLeaveRequest({
        id: options.id,
        comment: options.formData.note,
      })
    }

    return approveLeaveRequest({
      id: options.id,
      comment: options.formData.note,
      type,
    })
  }

  if (options.type === 'externalServiceRequest') {
    return approveExternalServiceRequest({
      id: options.id,
      comment: options.formData.note,
    })
  }
}

/**
 * Reject
 */
const rejectLeaveRequestMutation = useMutation(RejectLeaveRequestDocument)

async function rejectLeaveRequest(
  variables: RejectLeaveRequestMutationVariables
) {
  try {
    await rejectLeaveRequestMutation.mutate(variables)
  } catch (error) {
    throwFriendlyError(error)
  }
}

const rejectCancelLeaveRequestMutation = useMutation(
  RejectCancelLeaveRequestDocument
)

async function rejectCancelLeaveRequest(
  variables: RejectCancelLeaveRequestMutationVariables
) {
  try {
    await rejectCancelLeaveRequestMutation.mutate(variables)
  } catch (error) {
    throwFriendlyError(error)
  }
}

const rejectExternalServiceRequestMutation = useMutation(
  RejectExternalServiceRequestDocument
)

async function rejectExternalServiceRequest(
  variables: RejectLeaveRequestMutationVariables
) {
  try {
    await rejectExternalServiceRequestMutation.mutate(variables)
  } catch (error) {
    throwFriendlyError(error)
  }
}

function onReject() {
  if (!props.selectedRequestId)
    throw new Error('selectedRequestId is undefined')
  if (!props.selectedRequestType)
    throw new Error('selectedRequestType is undefined')
  if (props.selectedRequestType === 'expenseReport') return

  xstate.value.send({
    type: 'reject',
    variables: {
      id: props.selectedRequestId,
      type: props.selectedRequestType,
      operation: operation.value,
    },
  })
}

export type RejectRequestVariables = {
  id: number
  type: 'leaveRequest' | 'externalServiceRequest'
  operation: typeof operation.value
  formData: EmitsDialogRequestRejectPure['submit']
}

async function rejectRequest(options: RejectRequestVariables) {
  if (options.type === 'leaveRequest') {
    if (options.operation === 'processCancelLeaveRequest') {
      return rejectCancelLeaveRequest({
        id: options.id,
        comment: options.formData.note,
      })
    }

    return rejectLeaveRequest({
      id: options.id,
      comment: options.formData.note,
    })
  }

  if (options.type === 'externalServiceRequest') {
    return rejectExternalServiceRequest({
      id: options.id,
      comment: options.formData.note,
    })
  }
}

// /**
//  * Confirm Cancel
//  */
// const confirmCancelLeaveRequestMutation = useMutation(
//   ConfirmCancelLeaveRequestDocument
// )

// async function confirmCancelLeaveRequest(
//   variables: ConfirmCancelLeaveRequestMutationVariables
// ) {
//   try {
//     await confirmCancelLeaveRequestMutation.mutate(variables)
//   } catch (error) {
//     throwFriendlyError(error)
//   }
// }

// function onConfirmCancel() {
//   if (!props.selectedRequestId)
//     throw new Error('selectedRequestId is undefined')
//   if (!props.selectedRequestType)
//     throw new Error('selectedRequestType is undefined')
//   if (props.selectedRequestType !== 'leaveRequest') return

//   xstate.value.send({
//     type: 'confirmCancelLeaveRequest',
//     variables: { id: props.selectedRequestId },
//   })
// }

// export type ConfirmCancelVariables = {
//   id: number
//   formData: EmitsDialogRequestApprovePure['submit']
// }

// async function confirmCancelRequest(options: ConfirmCancelVariables) {
//   return confirmCancelLeaveRequest({
//     id: options.id,
//     comment: options.formData.note,
//   })
// }

// /**
//  * Reject Cancel
//  */
// const rejectCancelLeaveRequestMutation = useMutation(
//   RejectCancelLeaveRequestDocument
// )

// async function rejectCancelLeaveRequest(
//   variables: RejectCancelLeaveRequestMutationVariables
// ) {
//   try {
//     await rejectCancelLeaveRequestMutation.mutate(variables)
//   } catch (error) {
//     throwFriendlyError(error)
//   }
// }

// function onRejectCancel() {
//   if (!props.selectedRequestId)
//     throw new Error('selectedRequestId is undefined')
//   if (!props.selectedRequestType)
//     throw new Error('selectedRequestType is undefined')
//   if (props.selectedRequestType !== 'leaveRequest') return

//   xstate.value.send({
//     type: 'rejectCancelLeaveRequest',
//     variables: { id: props.selectedRequestId },
//   })
// }

// export type RejectCancelVariables = {
//   id: number
//   formData: EmitsDialogRequestRejectPure['submit']
// }

// async function rejectCancelRequest(options: RejectCancelVariables) {
//   return rejectCancelLeaveRequest({
//     id: options.id,
//     comment: options.formData.note,
//   })
// }
</script>
